import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IEmployeeAppInboxMessage,
  IEmployeeAppInboxMoment,
  IEmployeeAppInboxResponse,
  IEmployeeAppInboxTask,
  IEmployeeAppPinnedItemsResponse,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { compact, partition } from 'lodash';
import { useMemo } from 'react';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { now, tsIsAfter } from '../../../../../../services/time';
import { DoneCelebration } from '../../components/DoneCelebration';
import {
  NoArchivedMessages,
  NoArchivedMoments,
  NoCompletedTasks,
  NoCurrentTasksButOverdue,
  NoOptionalTasks,
  NoOverdueTasks,
  NoRecentMoments,
  NoUnarchivedMessages,
  NoUpcomingMessages,
  NoUpcomingTasks
} from '../../components/EmptyStatesAndHints';
import {
  InboxNavGroupKey,
  InboxNavItemKey,
  NavBarGroup,
  NavBarItem,
  withMarkedSelection
} from '../../components/Nav/InboxNav';
import { getInboxUrl } from '../../services/routes';
import { MessageGroup, TaskGroup } from './types';

// Default groups are useful if you want to show specific groups that could be empty.
// This is e.g. used with pinned items.
export const groupTasks = (
  ds: IEmployeeAppInboxTask[],
  defaultGroups: TaskGroup[] = []
): TaskGroup[] => {
  const toKey = (templateId: string, taskTemplateId: string) =>
    `${templateId}-${taskTemplateId}`;
  const res: {
    [key: string]: TaskGroup;
  } = {};
  defaultGroups.forEach((d) => {
    const key = toKey(d.templateId, d.taskTemplateId);
    res[key] = d;
  });
  ds.forEach((d) => {
    const { taskTemplateId, taskTitle, templateId, templateTitle } = d;
    const key = toKey(templateId, taskTemplateId);
    const container = (res[key] = res[key] || {
      taskTemplateId,
      taskTitle,
      templateId,
      templateTitle,
      tasks: []
    });
    container.tasks.push(d);
  });
  return Object.values(res);
};

// Should also be unified with groupTasks perhaps
export const groupMessages = (
  ds: IEmployeeAppInboxMessage[]
): MessageGroup[] => {
  const res: {
    [key: string]: MessageGroup;
  } = {};
  ds.forEach((d) => {
    const { msgTemplateId, msgSubject, templateId, templateTitle } = d;
    const key = `${templateId}-${msgTemplateId}`;
    const container = (res[key] = res[key] || {
      msgTemplateId,
      msgTitle: msgSubject,
      templateId,
      templateTitle,
      messages: []
    });
    container.messages.push(d);
  });
  return Object.values(res);
};

const SHOW_UPCOMING_MESSAGES = false;

const getNavGroups = (
  data: IEmployeeAppInboxResponse,
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'],
  ut: UnsafeTFunction,
  style: 'bar' | 'dropdown',
  u: string[],
  t: Set<string>
): NavBarGroup<InboxNavGroupKey, InboxNavItemKey>[] => {
  const withHrefFromKey = (
    partialItem: Omit<NavBarItem<InboxNavItemKey>, 'href'>
  ): NavBarItem<InboxNavItemKey> => ({
    ...partialItem,
    href: getInboxUrl(data.userAccessToken, {
      g: partialItem.key,
      u,
      t: Array.from(t)
    })
  });

  const getTasksWithStatus = (statuses: TaskStatus[]) =>
    data.tasks.filter((x) => statuses.includes(x.status));

  const todo = getTasksWithStatus([TaskStatus.RUNNING]);
  const upcoming = getTasksWithStatus([TaskStatus.NOT_STARTED]);
  const late = getTasksWithStatus([TaskStatus.LATE]);
  const optional = getTasksWithStatus([TaskStatus.PAST_DEADLINE]);
  const complete = getTasksWithStatus([
    TaskStatus.COMPLETE,
    TaskStatus.SKIPPED
  ]);

  // const msgsStatus = getMessageStatuses(instance);
  // const msgs = instance.data.messages;
  // const msgsCurrent = msgs.filter(
  //   (m) => msgsStatus[m.templateId] === 'CURRENT'
  // );
  // const msgsForLater = msgs.filter((m) => msgsStatus[m.templateId] === 'LATER');
  // const msgsArchived = msgs.filter(
  //   (m) => msgsStatus[m.templateId] === 'ARCHIVED'
  // );

  // TODO - the archival status of these is NOT correct yet
  const n = now();
  const msgs: {
    current: IEmployeeAppInboxMessage[];
    forLater: IEmployeeAppInboxMessage[];
    archived: IEmployeeAppInboxMessage[];
  } = {
    current: [],
    forLater: [],
    archived: []
  };
  data.messages.forEach((m) => {
    if (
      m.markedAsRead?.read ||
      (m.archived && m.markedAsRead?.read !== false)
    ) {
      msgs.archived.push(m);
    } else if (tsIsAfter(n, m.sendAt)) {
      msgs.current.push(m);
    } else {
      msgs.forLater.push(m);
    }
  });
  const pinnedTaskKeys = compact(
    pinnedItems.map((x) =>
      x.type === 'TASK' ? `${x.templateId}-${x.itemId}` : null
    )
  );

  const [momentsActive, momentsArchived] = partition(
    data.moments,
    (m) => !m.markedAsRead
  );
  const showMomentsArchive = !!(
    momentsArchived.length || data.hasArchivedMoments
  );
  const showMomentsSection = !!(momentsActive.length || showMomentsArchive);
  return compact([
    pinnedItems.length
      ? {
          key: 'pinned',
          title: undefined,
          items: compact<Omit<NavBarItem<InboxNavItemKey>, 'href'>>([
            {
              key: 'pinned-tasks',
              title: ut('Pinned tasks'),
              color: 'gray',
              count: [...todo, ...late].filter((x) =>
                pinnedTaskKeys.includes(`${x.templateId}-${x.taskTemplateId}`)
              ).length,
              trackingKey: 'Pinned tasks'
            }
          ]).map(withHrefFromKey)
        }
      : null,
    {
      key: 'inProgress',
      title: ut('In progress'),
      items: compact<Omit<NavBarItem<InboxNavItemKey>, 'href'>>([
        {
          key: 'everything',
          title:
            style === 'bar' ? ut('Everything') : ut('Everything in progress'),
          color: 'gray',
          count: todo.length + late.length, // + msgs.current.length, // messages are currently not shown here
          hideCount: style === 'bar',
          trackingKey: 'Everything'
        },
        late.length && {
          key: 'overdue',
          title: ut('Overdue tasks'),
          color: 'red',
          count: late.length,
          trackingKey: 'Overdue tasks'
        },
        {
          key: 'todo',
          title: ut('To do tasks'),
          color: 'blue',
          count: todo.length,
          selected: true,
          trackingKey: 'To do tasks'
        },
        {
          key: 'msgs',
          title: ut('Messages'),
          color: 'gray',
          count: msgs.current.length,
          selected: false,
          trackingKey: 'Messages'
        }
      ]).map(withHrefFromKey)
    },
    {
      key: 'forLater',
      title: ut('For later'),
      collapsible: true,
      items: compact([
        {
          key: 'upcoming' as const,
          title: ut('Upcoming tasks'),
          color: 'gray' as const,
          count: upcoming.length,
          trackingKey: 'Upcoming tasks'
        },
        SHOW_UPCOMING_MESSAGES && {
          key: 'upcoming-msgs' as const,
          title: ut('Upcoming messages'),
          color: 'gray' as const,
          count: msgs.forLater.length,
          selected: false,
          trackingKey: 'Upcoming Messages'
        }
      ]).map(withHrefFromKey)
    },
    {
      key: 'archive',
      title: ut('Archived'),
      items: compact<Omit<NavBarItem<InboxNavItemKey>, 'href'>>([
        {
          key: 'complete',
          title: ut('Completed tasks'),
          color: 'green',
          count: complete.length,
          selected: false,
          trackingKey: 'Completed tasks'
        },
        optional.length && {
          key: 'optional',
          title: ut('Optional tasks'),
          color: 'gray',
          count: optional.length,
          selected: false,
          trackingKey: 'Optional tasks'
        },
        {
          key: 'archived-msgs',
          title: ut('Messages'),
          color: 'gray',
          count: msgs.archived.length,
          selected: false,
          trackingKey: 'Archived messages'
        }
      ]).map(withHrefFromKey)
    },
    showMomentsSection && {
      key: 'moments',
      title: ut('News'),
      items: compact<Omit<NavBarItem<InboxNavItemKey>, 'href'>>([
        {
          key: 'moments-active',
          title: style === 'dropdown' ? ut('Recent news') : ut('Recent'),
          color: 'blue',
          count: momentsActive.length,
          trackingKey: 'Active moments'
        },
        showMomentsArchive && {
          key: 'moments-archived',
          title: style === 'dropdown' ? ut('Archived news') : ut('Archived'),
          color: 'gray',
          count: momentsArchived.length,
          hideCount: true, // NEVER show the count, as this section is special and makes an additional quest for time-based archivals
          trackingKey: 'Archived moments'
        }
      ]).map(withHrefFromKey)
    }
  ]);
};

export const useNavGroups = (
  data: IEmployeeAppInboxResponse,
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'],
  g: string,
  style: 'bar' | 'dropdown',
  u: string[],
  t: Set<string>
) => {
  const { t: ut } = useUnsafeTranslation();
  return useMemo<NavBarGroup<InboxNavGroupKey, InboxNavItemKey>[]>(() => {
    const groups = getNavGroups(data, pinnedItems, ut, style, u, t);
    return withMarkedSelection(groups, g);
  }, [data, pinnedItems, ut, g, style, u, t]);
};

const NoTasksForYou = () => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <GrayText>{t('There are currently no tasks for you.')}</GrayText>
      <GrayText>
        {t(
          'This task list is dynamically tailored to you, so it will update as it adapts to your sitation.'
        )}
      </GrayText>
    </>
  );
};

const AllCurrentTasksDone = ({ anyTasksAtAll }: { anyTasksAtAll: boolean }) => {
  const { t } = useUnsafeTranslation();
  if (!anyTasksAtAll) {
    return <NoTasksForYou />;
  }
  return (
    <DoneCelebration title={t(`You're done for now`)}>
      <Typography>{t('All your current tasks are completed')}</Typography>
    </DoneCelebration>
  );
};

const NoPinnedTasks = () => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <GrayText>{t('There are currently no pinned tasks for you.')}</GrayText>
      <GrayText>
        {t(
          'This task list is dynamically tailored to you, so it will update as it adapts to your sitation.'
        )}
      </GrayText>
    </>
  );
};

const GrayText = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w500
}));

export const useGroupedInboxData = (
  data: IEmployeeAppInboxResponse,
  pinnedItems: IEmployeeAppPinnedItemsResponse['items'],
  g: InboxNavItemKey | null,
  linkToOverdueSection?: string
) => {
  const { t } = useUnsafeTranslation();
  return useMemo<{
    taskGroups: TaskGroup[];
    messageGroups: MessageGroup[];
    moments: IEmployeeAppInboxMoment[];
    title: string;
    emptyState: React.ReactNode;
  }>(() => {
    const n = now();
    const getSelected = (): {
      tasks: IEmployeeAppInboxTask[];
      messages: IEmployeeAppInboxMessage[];
      moments: IEmployeeAppInboxMoment[];
      title: string;
      emptyState: React.ReactNode;
    } => {
      const anyTasksAtAll = data.tasks.length > 0;
      const getTasksWithStatus = (statuses: TaskStatus[]) =>
        data.tasks.filter((x) => statuses.includes(x.status));

      if (g === null) {
        // This is an intermediary state which might be used to determine where
        // to go to - it might never get called, as other parts of the application
        // should establish the presence of a group
        return {
          tasks: data.tasks,
          messages: data.messages,
          moments: data.moments,
          title: '',
          emptyState: <div />
        };
      }

      if (g === 'pinned-tasks') {
        const pinnedTaskKeys = compact(
          pinnedItems.map((x) =>
            x.type === 'TASK' ? `${x.templateId}-${x.itemId}` : null
          )
        );
        return {
          tasks: getTasksWithStatus([
            TaskStatus.RUNNING,
            TaskStatus.LATE
          ]).filter((x) =>
            pinnedTaskKeys.includes(`${x.templateId}-${x.taskTemplateId}`)
          ),
          messages: EMPTY_ARR,
          moments: EMPTY_ARR,
          title: t('Pinned tasks'),
          // Not really shown, as there will always be empty pinned groups around
          emptyState: <NoPinnedTasks />
        };
      }

      if (g === 'everything') {
        return {
          tasks: getTasksWithStatus([TaskStatus.RUNNING, TaskStatus.LATE]),
          messages: EMPTY_ARR,
          moments: EMPTY_ARR,
          title: t('All current tasks'),
          emptyState: <AllCurrentTasksDone anyTasksAtAll={anyTasksAtAll} />
        };
      }

      if (g === 'overdue') {
        return {
          tasks: getTasksWithStatus([TaskStatus.LATE]),
          messages: EMPTY_ARR,
          moments: EMPTY_ARR,
          title: t('Overdue tasks'),
          emptyState: <NoOverdueTasks />
        };
      }
      if (g === 'todo') {
        const overdueTasks = getTasksWithStatus([TaskStatus.LATE]);
        const anyOverdueTasks = overdueTasks.length > 0;
        return {
          tasks: getTasksWithStatus([TaskStatus.RUNNING]),
          messages: EMPTY_ARR,
          moments: EMPTY_ARR,
          title: t('To do tasks'),
          emptyState: anyOverdueTasks ? (
            <NoCurrentTasksButOverdue to={linkToOverdueSection} />
          ) : (
            <AllCurrentTasksDone anyTasksAtAll={anyTasksAtAll} />
          )
        };
      }

      if (g === 'upcoming') {
        return {
          tasks: getTasksWithStatus([TaskStatus.NOT_STARTED]),
          messages: EMPTY_ARR,
          moments: EMPTY_ARR,
          title: t('Upcoming tasks'),
          emptyState: <NoUpcomingTasks />
        };
      }

      if (g === 'complete') {
        return {
          tasks: getTasksWithStatus([TaskStatus.COMPLETE, TaskStatus.SKIPPED]),
          messages: EMPTY_ARR,
          moments: EMPTY_ARR,
          title: t('Completed tasks'),
          emptyState: <NoCompletedTasks />
        };
      }

      if (g === 'optional') {
        return {
          tasks: getTasksWithStatus([TaskStatus.PAST_DEADLINE]),
          messages: EMPTY_ARR,
          moments: EMPTY_ARR,
          title: t('Optional tasks'),
          emptyState: <NoOptionalTasks />
        };
      }

      if (g === 'msgs') {
        return {
          tasks: EMPTY_ARR,
          messages: data.messages.filter(
            (x) =>
              tsIsAfter(n, x.sendAt) && !x.archived && !x.markedAsRead?.read
          ),
          moments: EMPTY_ARR,
          title: t('Messages'),
          emptyState: <NoUnarchivedMessages />
        };
      }

      if (g === 'upcoming-msgs') {
        return {
          tasks: EMPTY_ARR,
          messages: data.messages.filter(
            (x) =>
              tsIsAfter(x.sendAt, n) && !x.archived && !x.markedAsRead?.read
          ),
          moments: EMPTY_ARR,
          title: t('Upcoming messages'),
          emptyState: <NoUpcomingMessages />
        };
      }

      if (g === 'archived-msgs') {
        return {
          tasks: EMPTY_ARR,
          // Explicit false checking for explicit opt outs
          messages: data.messages.filter(
            (x) =>
              x.markedAsRead?.read ||
              (x.archived && x.markedAsRead?.read !== false)
          ),
          moments: EMPTY_ARR,
          title: t('Archived messages'),
          emptyState: <NoArchivedMessages />
        };
      }

      if (g === 'moments-active') {
        return {
          tasks: EMPTY_ARR,
          messages: EMPTY_ARR,
          moments: data.moments.filter((m) => !m.markedAsRead),
          title: t('Recent news'),
          emptyState: <NoRecentMoments />
        };
      }

      if (g === 'moments-archived') {
        return {
          tasks: EMPTY_ARR,
          messages: EMPTY_ARR,
          moments: data.moments.filter((m) => m.markedAsRead),
          title: t('Archived news'),
          emptyState: <NoArchivedMoments />
        };
      }

      return {
        tasks: EMPTY_ARR,
        messages: EMPTY_ARR,
        moments: EMPTY_ARR,
        title: '',
        emptyState: <div />
      };
    };
    const _d = getSelected();
    const defaultTaskGroups: TaskGroup[] =
      g === 'pinned-tasks'
        ? compact(
            pinnedItems.map((item) => {
              if (item.type !== 'TASK') {
                return null;
              }
              return {
                taskTemplateId: item.itemId,
                taskTitle: item.title,
                templateId: item.templateId,
                templateTitle: item.templateTitle,
                tasks: []
              };
            })
          )
        : [];
    return {
      taskGroups: groupTasks(_d.tasks, defaultTaskGroups),
      messageGroups: groupMessages(_d.messages),
      moments: _d.moments,
      title: _d.title,
      emptyState: _d.emptyState
    };
  }, [data, pinnedItems, g, linkToOverdueSection, t]);
};
